import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightDocumentUpload from '../../assets/landing_page_images/Light-DocumentUploadFlow.webp';
import DarkDocumentUpload from '../../assets/landing_page_images/Dark-DocumentUploadFlow.webp';
import LightManageUser from '../../assets/landing_page_images/Light-ManageUser.webp';
import DarkManageUser from '../../assets/landing_page_images/Dark-ManageUser.webp';
import Mobile1 from '../../assets/landing_page_images/mobile1.webp';
import Mobile2 from '../../assets/landing_page_images/mobile2.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import './ConstructionSafetyComplianceSoftware.css';

const KEY_CAPABILITIES = [
  {
    title: 'WHS document management',
    icon: 'folder',
    text: 'Store policies, procedures, SWMS, risk assessments, WHS plans, registers and compliance records with version and expiry tracking.',
  },
  {
    title: 'Safety inspections and audits',
    icon: 'clipboard',
    text: 'Conduct inspections and audits as part of your wider compliance workflow.',
  },
  {
    title: 'Digital WHS forms',
    icon: 'form',
    text: 'Build forms with dynamic fields, signatures and mobile access for activities such as site inductions, toolbox talks, pre-start checks, hazard reports and site inspections.',
  },
  {
    title: 'QR-based forms',
    icon: 'qr',
    text: 'Workers can access relevant forms through QR codes on site.',
  },
  {
    title: 'Corrective action tracking',
    icon: 'task',
    text: 'Assign actions, set priorities and statuses, and add attachments or comments.',
  },
  {
    title: 'Site management',
    icon: 'pin',
    text: 'Maintain site records and connect site documents and digital form submissions to the relevant location.',
  },
  {
    title: 'Plant and equipment registers',
    icon: 'plant',
    text: 'Track vehicles, plant, equipment and tools, including service intervals, registration expiry, condition, location and assignments.',
  },
];

const WORKFLOW_STEPS = [
  {
    title: 'Set up your sites',
    icon: 'pin',
    text: 'and maintain location-specific records.',
  },
  {
    title: 'Upload WHS documents',
    icon: 'folder',
    text: 'such as SWMS, risk assessments, policies and plans.',
  },
  {
    title: 'Create digital forms',
    icon: 'form',
    text: 'for inspections, inductions, pre-starts and other site activities.',
  },
  {
    title: 'Complete inspections and record findings',
    icon: 'clipboard',
    text: 'from the field.',
  },
  {
    title: 'Create corrective actions',
    icon: 'task',
    text: 'and assign responsibility.',
  },
  {
    title: 'Track tasks and documents',
    icon: 'dashboard',
    text: 'through the compliance dashboard.',
  },
];

const LOOK_FOR = [
  { text: 'Safety inspections and audits', icon: 'clipboard' },
  { text: 'WHS documents and SWMS', icon: 'folder' },
  { text: 'Digital forms and QR access', icon: 'qr' },
  { text: 'Hazards and incidents', icon: 'hazard' },
  { text: 'Corrective actions and tasks', icon: 'task' },
  { text: 'Construction sites', icon: 'pin' },
  { text: 'Plant and equipment records', icon: 'plant' },
  { text: 'Worker and contractor compliance', icon: 'users' },
  { text: 'Document expiry and review dates', icon: 'calendar' },
  { text: 'Mobile access', icon: 'mobile' },
];

const SPEC_TABLE = [
  { capability: 'WHS document management', value: 'Yes' },
  { capability: 'SWMS and risk assessment storage', value: 'Yes' },
  { capability: 'Safety inspections and audits', value: 'Yes' },
  { capability: 'Digital WHS forms', value: 'Yes' },
  { capability: 'QR-based forms', value: 'Yes' },
  { capability: 'Corrective actions', value: 'Yes' },
  { capability: 'Task management', value: 'Yes' },
  { capability: 'Multi-site management', value: 'Yes' },
  { capability: 'Plant and equipment registers', value: 'Yes' },
  { capability: 'Worker and contractor records', value: 'Yes' },
  { capability: 'Mobile apps', value: 'Android and iOS' },
  { capability: 'AI compliance assistance', value: 'Yes' },
  { capability: 'Free trial', value: 'Available without card details' },
];

const FAQ_ITEMS = [
  {
    question: 'What is construction safety compliance software?',
    answer:
      'It is software that helps construction businesses manage WHS documents, inspections, forms, corrective actions, tasks, sites, workers, contractors and other safety records in one system.',
  },
  {
    question: 'Can Complimate manage SWMS?',
    answer:
      'Complimate supports storing and managing SWMS as part of its WHS document management functionality, including document versions, review dates and expiry tracking.',
  },
  {
    question: 'Can workers complete safety forms on site?',
    answer:
      "Yes. Complimate's digital forms are designed for mobile use and can be accessed through QR codes for site activities such as inductions, toolbox talks, pre-starts and inspections.",
  },
  {
    question: 'Can Complimate track corrective actions?',
    answer:
      'Yes. Corrective actions can be assigned, prioritised and tracked as part of the projects and task workflow.',
  },
  {
    question: 'Can I manage multiple construction sites?',
    answer:
      'Yes. Complimate includes a site register with site-specific documents and linked digital form submissions, supporting multi-site compliance management.',
  },
  {
    question: 'Does Complimate replace professional WHS advice?',
    answer:
      'No. Software can help organise and manage compliance activities, but businesses should obtain appropriate professional WHS or legal advice where required. The product documentation specifically positions Complimate as a practical WHS management platform rather than a replacement for professional advice.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/construction-safety-compliance/');

function CheckIcon() {
  return (
    <span className="seo-check" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3.75 9.75L7.125 13.125L14.25 4.875"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

const ICON_PATHS = {
  hardhat: (
    <>
      <path d="M4 14a8 8 0 0 1 16 0" />
      <path d="M3 14h18v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3Z" />
      <path d="M12 4v3" />
      <path d="M9 19h6" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />,
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  clipboard: (
    <>
      <path d="M8 4h8" />
      <path d="M9 3v3h6V3" />
      <path d="M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  form: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h4v4h-4zM20 14v3M14 20h3" />
    </>
  ),
  task: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8 12 2.4 2.4L16 9" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  plant: (
    <>
      <path d="M3 16h12v-5H8.5L6 8H3v8Z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="13" cy="18" r="2" />
      <path d="M15 13h3.5L21 16v2h-6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6" />
      <path d="M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  hazard: (
    <>
      <path d="M12 3 22 20H2L12 3Z" />
      <path d="M12 9v5" />
      <path d="M12 17h.01" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6" />
      <path d="M12 8h.01" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="csc-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.hardhat}
      </svg>
    </span>
  );
}

function ConstructionSafetyComplianceSoftware() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="csc-page">
        <section className="csc-hero">
          <Container>
            <div className="csc-hero-grid">
              <div className="csc-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="hardhat" />
                  Construction Safety Compliance
                </p>
                <h1 className="seo-h1">Construction Safety Compliance Software</h1>
                <p className="seo-lead">
                  Complimate is construction safety compliance software for
                  Australian businesses that need to manage WHS documents, site
                  inspections, digital forms, corrective actions, tasks, workers,
                  contractors, sites and assets in one platform. It is designed to
                  help construction teams move away from scattered paperwork,
                  spreadsheets and disconnected records while keeping important
                  compliance information organised.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to="/contact-us"
                    className="btn-teal csc-primary-button"
                    id="csc_book_demo_hero"
                  >
                    Book a Demo
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>

              <div className="csc-hero-visual">
                <div className="csc-visual-glow" aria-hidden="true" />
                <div className="csc-product-window">
                  <div className="csc-window-bar" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                  <img
                    src={isDark ? DarkDashboard : LightDashboard}
                    alt="Complimate compliance dashboard for construction safety documents, inspections and tasks"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>
                <div className="csc-float-card csc-float-card-top" aria-hidden="true">
                  <FeatureIcon name="clipboard" />
                  <span>Safety inspections</span>
                </div>
                <div className="csc-float-card csc-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="form" />
                  <span>Digital WHS forms</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section csc-intro-section">
          <Container>
            <div className="csc-intro-grid">
              <div>
                <div className="csc-section-marker">
                  <FeatureIcon name="shield" />
                </div>
                <h2 className="seo-h2">
                  What Is Construction Safety Compliance Software?
                </h2>
                <p className="seo-body">
                  Construction safety compliance software helps businesses manage
                  the records, processes and activities that support workplace
                  health and safety across construction operations.
                </p>
                <p className="seo-body">
                  Complimate brings key parts of a safety management system
                  together, including WHS documentation, inspections and audits,
                  digital forms, hazards, incidents, corrective actions, site
                  management, worker and contractor records, and plant and asset
                  registers.
                </p>
                <p className="seo-body">
                  This gives WHS managers, site supervisors and operations teams a
                  central place to manage compliance work across one or multiple
                  sites.
                </p>
              </div>
              <div className="csc-shot-frame">
                <img
                  src={isDark ? DarkDocument : LightDocument}
                  alt="Complimate document hub for WHS policies, SWMS, procedures and compliance records"
                  width="960"
                  height="680"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section csc-help-section">
          <Container>
            <div className="csc-section-intro">
              <h2 className="seo-h2">How Can Complimate Help Construction Teams?</h2>
              <p className="seo-body">
                Construction compliance often involves more than completing a
                checklist. A finding from an inspection may need an owner,
                corrective action, supporting evidence and follow-up.
              </p>
              <p className="seo-body">
                Complimate connects these activities through its projects and task
                workflow. For example, findings can be turned into assigned
                corrective actions and tracked through to completion.
              </p>
              <p className="seo-body csc-label">
                <strong>Key capabilities include:</strong>
              </p>
            </div>
            <div className="csc-capability-grid">
              {KEY_CAPABILITIES.map((item, index) => (
                <article className="csc-capability-card" key={item.title}>
                  <div className="csc-card-topline">
                    <FeatureIcon name={item.icon} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  <p className="seo-body">{item.text}</p>
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section csc-itp-section">
          <Container>
            <div className="csc-itp-grid">
              <div className="csc-shot-frame csc-shot-frame-soft">
                <img
                  src={isDark ? DarkDocumentUpload : LightDocumentUpload}
                  alt="Complimate digital forms and document workflow for construction safety inspections"
                  width="960"
                  height="680"
                />
              </div>
              <div>
                <div className="csc-section-marker">
                  <FeatureIcon name="clipboard" />
                </div>
                <h2 className="seo-h2">
                  Can Complimate Support Safety Inspections and ITP Construction
                  Workflows?
                </h2>
                <p className="seo-body">
                  Complimate supports safety inspections and audits through digital
                  forms, site records, findings and corrective actions.
                </p>
                <div className="csc-note">
                  <FeatureIcon name="info" />
                  <p className="seo-body">
                    For construction teams managing ITP construction documentation,
                    relevant project and site documents can be maintained within the
                    broader document and site management workflow. However, the
                    supplied product documentation does not identify a dedicated ITP
                    module, so ITP-specific functionality should not be represented as
                    a standalone Complimate feature.
                  </p>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section csc-workflow-section">
          <Container>
            <div className="csc-section-intro">
              <h2 className="seo-h2">
                How Does Construction Safety Compliance Software Work?
              </h2>
              <p className="seo-body">A typical workflow can look like this:</p>
            </div>
            <div className="csc-steps">
              {WORKFLOW_STEPS.map((step, index) => (
                <article key={step.title} className="csc-step">
                  <div className="csc-step-number">{index + 1}</div>
                  <FeatureIcon name={step.icon} />
                  <div>
                    <h3 className="seo-h4">
                      {index + 1}. {step.title}
                    </h3>
                    <p className="seo-body mb-0">{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="csc-mobile-note">
              <div className="csc-mobile-copy">
                <FeatureIcon name="mobile" />
                <p className="seo-body mb-0">
                  Complimate also provides Android and iOS mobile apps for compliance
                  work away from the office.
                </p>
              </div>
              <div className="csc-mobile-shots" aria-hidden="true">
                <img src={Mobile1} alt="" width="220" height="440" />
                <img src={Mobile2} alt="" width="220" height="440" />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section csc-cta-section">
          <Container>
            <div className="csc-cta-card">
              <div className="csc-cta-orb csc-cta-orb-one" aria-hidden="true" />
              <div className="csc-cta-orb csc-cta-orb-two" aria-hidden="true" />
              <div className="csc-cta-copy">
                <h2 className="seo-h2">Manage Construction Compliance in One Place</h2>
                <p className="seo-body">
                  Bring your construction documents, inspections, digital forms,
                  corrective actions, sites and safety records into one practical
                  compliance platform.
                </p>
              </div>
              <Button
                as={Link}
                to="/contact-us"
                className="btn-teal csc-cta-button"
                id="csc_book_demo_mid"
              >
                Book a Demo
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section csc-look-section">
          <Container>
            <div className="csc-look-intro">
              <div>
                <div className="csc-section-marker">
                  <FeatureIcon name="users" />
                </div>
                <h2 className="seo-h2">
                  What Should You Look for in Safety Compliance Software?
                </h2>
                <p className="seo-body">
                  When comparing construction safety compliance software, look for
                  more than digital checklists. Consider whether the platform can
                  connect:
                </p>
              </div>
              <div className="csc-shot-frame">
                <img
                  src={isDark ? DarkManageUser : LightManageUser}
                  alt="Complimate worker and contractor records for construction compliance"
                  width="960"
                  height="680"
                />
              </div>
            </div>
            <ul className="csc-look-grid">
              {LOOK_FOR.map((item) => (
                <li className="csc-look-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <p className="seo-body csc-look-note">
              Complimate is positioned as an all-in-one WHS and compliance
              management platform for small and medium businesses, with these
              functions connected rather than managed as isolated tools.
            </p>
          </Container>
        </section>

        <section className="seo-section csc-spec-section">
          <Container>
            <div className="seo-section-header csc-centered-heading">
              <h2 className="seo-h2">
                Construction Safety Compliance Software: Key Specifications
              </h2>
            </div>
            <div className="seo-table-wrap csc-spec-table">
              <table className="seo-table">
                <thead>
                  <tr>
                    <th scope="col">Capability</th>
                    <th scope="col">Complimate</th>
                  </tr>
                </thead>
                <tbody>
                  {SPEC_TABLE.map((row) => (
                    <tr key={row.capability}>
                      <th scope="row">{row.capability}</th>
                      <td>
                        {row.value === 'Yes' ? (
                          <span className="csc-spec-yes">
                            <CheckIcon />
                            Yes
                          </span>
                        ) : (
                          row.value
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="seo-body seo-diff-note">
              The functionality and positioning above are based on the September
              2026 Complimate product documentation.
            </p>
          </Container>
        </section>

        <section className="seo-section csc-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default ConstructionSafetyComplianceSoftware;
